import React, { createContext, useContext, useState } from 'react';

type Currency = 'NGN' | 'USD';

export interface BookingItem {
    id: string;
    type: 'hotel' | 'flight';
    details: any;
    price: number;           // Retail price in NGN
    rawPriceUSD?: number;    // Original USD price from LetSFG
    search_id?: string;      // LetSFG search_id for booking
    offer_id?: string;       // LetSFG offer_id for booking
    session_id?: string;     // LetSFG hotel session_id
}

interface BookingContextType {
    currency: Currency;
    cart: BookingItem[];
    addToCart: (item: Omit<BookingItem, 'id'>) => void;
    removeFromCart: (id: string) => void;
    clearCart: () => void;
    formatPrice: (amountNGN: number) => string;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currency] = useState<Currency>('NGN');
    const [cart, setCart] = useState<BookingItem[]>([]);

    const addToCart = (item: Omit<BookingItem, 'id'>) => {
        const newItem: BookingItem = { ...item, id: Math.random().toString(36).substr(2, 9) };
        setCart(prev => [...prev, newItem]);
    };

    const removeFromCart = (id: string) => {
        setCart(prev => prev.filter(item => item.id !== id));
    };

    const clearCart = () => {
        setCart([]);
    };

    const formatPrice = (amountNGN: number) => {
        if (currency === 'NGN') {
            return `₦${amountNGN.toLocaleString()}`;
        } else {
            const usd = amountNGN / 1600;
            return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
    };

    return (
        <BookingContext.Provider value={{ currency, cart, addToCart, removeFromCart, clearCart, formatPrice }}>
            {children}
        </BookingContext.Provider>
    );
};

export const useBooking = () => {
    const context = useContext(BookingContext);
    if (context === undefined) {
        throw new Error('useBooking must be used within a BookingProvider');
    }
    return context;
};
